/**
 * stopUtils.ts
 * 
 * Utility functions for journey stop operations.
 * Handles stop lookups, location formatting, and new stop detection.
 * 
 * A "stop" represents a location in the journey where the traveler visits.
 * In v2 format, stops are stored at the root level with order, city, country, etc.
 */

import type { Change } from '../../../contexts/JourneyEditContext';

/**
 * Gets the stop by order number.
 * 
 * @param journey - The journey object
 * @param order - The order number of the stop
 * @returns The stop object or null if not found
 */
export function getStopByOrder(journey: any, order: number): any | null {
  if (!journey?.stops) return null;
  return journey.stops.find((s: any) => s.order === order) || null;
}

/**
 * Gets the stop by ID.
 * 
 * @param journey - The journey object
 * @param stopId - The ID of the stop
 * @returns The stop object or null if not found
 */
export function getStopById(journey: any, stopId: string): any | null {
  if (!journey?.stops || !stopId) return null;
  return journey.stops.find((s: any) => s.id === stopId) || null;
}

/**
 * Gets stop location string (city, country format).
 * Handles both v1 (location/location_country) and v2 (city/country) field names.
 * 
 * @param stop - The stop object
 * @returns Formatted location string "City, Country" or "City" if no country
 */
export function getStopLocation(stop: any): string {
  if (!stop) return 'Unknown';
  
  // v2 format uses city/country
  const city = stop.city || stop.location || 'Unknown';
  const country = stop.country || stop.location_country || '';
  
  return country ? `${city}, ${country}` : city;
}

/**
 * Gets activities array from stop.
 * Handles both v1 (things_to_do) and v2 (activities) field names.
 * 
 * @param stop - The stop object
 * @returns Array of activity strings
 */
export function getStopActivities(stop: any): string[] {
  if (!stop) return [];
  return stop.activities || stop.things_to_do || [];
}

/**
 * Checks if a stop is newly added or replaced.
 * A stop is considered "new" if it was added or replaced via a pending change.
 * 
 * @param stopId - The ID of the stop
 * @param pendingChanges - Array of pending changes
 * @returns True if the stop is new or was replaced
 */
export function isStopNew(stopId: string, pendingChanges: Change[]): boolean {
  return pendingChanges.some((change: any) =>
    (change.type === 'add_stop' && change.stopId === stopId) ||
    (change.type === 'replace_stop' && change.stopId === stopId)
  );
}

/**
 * Checks if a stop is being removed.
 * 
 * @param stopId - The ID of the stop
 * @param pendingChanges - Array of pending changes
 * @returns True if the stop is marked for removal
 */
export function isStopBeingRemoved(stopId: string, pendingChanges: Change[]): boolean {
  return pendingChanges.some((change: any) =>
    change.type === 'remove_stop' && change.stopId === stopId
  );
}

/**
 * Gets the number of stops in the journey.
 * 
 * @param journey - The journey object
 * @returns Number of stops
 */
export function getStopCount(journey: any): number {
  return journey?.stops?.length || 0;
}

/**
 * Checks if a stop can be removed (must have more than 2 stops).
 * 
 * @param journey - The journey object
 * @returns True if a stop can be removed
 */
export function canRemoveStop(journey: any): boolean {
  return getStopCount(journey) > 2;
}

/**
 * Gets journey totals (handles both v1 snake_case and v2 camelCase field names).
 * 
 * @param journey - The journey object
 * @returns Object with cost, co2, and distance totals
 */
export function getJourneyTotals(journey: any) {
  return {
    cost: journey.totalTransportCostEur ?? journey.total_transport_cost_eur ?? 0,
    co2: journey.totalTransportCo2eKg ?? journey.total_transport_co2e_kg ?? 0,
    distance: journey.totalTransportDistanceKm ?? journey.total_transport_distance_km ?? 0
  };
}

/**
 * Gets journey metadata (handles both v1 and v2 field names).
 * 
 * @param journey - The journey object
 * @returns Object with origin, destination, durationDays, isRoundTrip, transportPreference
 */
export function getJourneyMeta(journey: any) {
  return {
    origin: journey.origin,
    destination: journey.destination,
    durationDays: journey.durationDays ?? journey.duration_days,
    isRoundTrip: journey.isRoundTrip ?? journey.is_round_trip,
    transportPreference: journey.transportPreference ?? journey.transport_preference ?? 'none'
  };
}

